import type { ReactNode, CSSProperties } from 'react';
import { useZoomNormalization } from './useZoomNormalization';

interface ZoomNormalizedContainerProps {
  children: ReactNode;
  /** Show debug info overlay */
  debug?: boolean;
  /** Custom styles for the container */
  style?: CSSProperties;
  /** Class name for additional styling */
  className?: string;
}

/**
 * A container that counter-scales its contents to maintain consistent
 * visual size regardless of browser zoom level.
 *
 * How it works:
 * 1. Detects the current zoom level
 * 2. Applies CSS transform: scale(1/zoom) to counter the zoom
 * 3. Adjusts the container dimensions to account for the scaled content
 */
export function ZoomNormalizedContainer({ children, style, className }: ZoomNormalizedContainerProps) {
  const { zoomLevel, inverseScale } = useZoomNormalization();

  // Container styles that apply inverse scaling
  const containerStyle: CSSProperties = {
    // Position at viewport origin to avoid body centering issues
    position: 'fixed',
    top: 0,
    left: 0,
    transformOrigin: 'top left',
    transform: `scale(${inverseScale})`,
    // Expand to compensate for scale-down
    width: `${100 * zoomLevel}vw`,
    height: `${100 * zoomLevel}vh`,
    overflow: 'auto',
    ...style,
  };

  return (
    <div style={containerStyle} className={className}>
      {children}
    </div>
  );
}
